// src/components/FinanceCRMGate.js
//
// Gate that wraps every /finance-portal/* route. The Finance Portal is
// scoped per CRM client (account) — every finance row carries
// crm_account_id. Without at least one CRM client, every finance page
// returns empty data and any "create" form fails server-side.
//
// Behavior on mount:
//   1. Hit /api/finance/crm-accounts?per_page=1 to count CRM clients.
//   2. If empty, render a modal popup that explains the user has no clients
//      and offers a button to navigate to the active chatbot's CRM Clients
//      page. The user must explicitly click — no automatic redirect.
//   3. Otherwise render <Outlet /> so the requested finance page mounts.

import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Stack, Typography,
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axiosInstance from '../utils/axiosInstance';
import { useChatbots } from '../contexts/ChatbotContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

export default function FinanceCRMGate() {
    const location = useLocation();
    const navigate = useNavigate();
    const { activeChatbot } = useChatbots();
    const [state, setState] = useState({ loading: true, hasClients: null });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/crm-accounts', {
                    params: { page: 1, per_page: 1 },
                });
                if (cancelled) return;
                const total = res.data?.total ?? (res.data?.data?.length ?? 0);
                setState({ loading: false, hasClients: total > 0 });
            } catch (err) {
                if (cancelled) return;
                // Fail-open: if the count call errors, let the user through and
                // let the underlying page surface the real problem.
                console.warn('[FinanceCRMGate] CRM count check failed:', err);
                setState({ loading: false, hasClients: true });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const goToClients = () => {
        const slug = activeChatbot?.chatbot_identity || activeChatbot?.id;
        if (slug) {
            navigate(`/chatbot/${slug}/crm/clients`, {
                state: {
                    fromFinancePortal: true,
                    intendedPath: location.pathname + location.search,
                    reason: 'Add a client first to start using the Finance Portal.',
                },
            });
        } else {
            navigate('/chatbots', { replace: false });
        }
    };

    const goBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate('/');
    };

    if (state.loading) {
        return (
            <Box sx={{
                minHeight: '60vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                bgcolor: '#f8fafc',
                fontFamily,
            }}>
                <CircularProgress size={32} />
                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>
                    Loading Finance Portal…
                </Typography>
            </Box>
        );
    }

    if (state.hasClients === false) {
        return (
            <>
                {/* Subtle backdrop content so the dialog isn't floating in a void */}
                <Box sx={{
                    minHeight: '60vh',
                    bgcolor: '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily,
                }}>
                    <Stack alignItems="center" spacing={1.5} sx={{ opacity: 0.55 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 56, color: '#94a3b8' }} />
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '1rem' }}>
                            Finance Portal
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                            Waiting for CRM client setup…
                        </Typography>
                    </Stack>
                </Box>

                <Dialog
                    open
                    onClose={() => { /* require explicit choice — no outside-click close */ }}
                    disableEscapeKeyDown
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
                >
                    <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '12px',
                            bgcolor: '#dbeafe', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                        }}>
                            <GroupAddIcon sx={{ color: '#1e40af' }} />
                        </Box>
                        No clients yet
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontFamily, fontSize: '0.95rem', color: '#1e293b', mb: 1.5 }}>
                            There are no clients in your CRM. Please add at least one
                            client before using the Finance Portal.
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                            Every finance record (invoice, bill, payment, journal entry,
                            bank account, reconciliation) is scoped to a CRM client. Once
                            you add your first client you can come back and pick up where
                            you left off
                            {location.pathname && location.pathname !== '/finance-portal' && (
                                <> at <Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: '#f1f5f9', px: 0.75, py: 0.25, borderRadius: '4px' }}>{location.pathname}</Box></>
                            )}
                            .
                        </Typography>
                        {!activeChatbot && (
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#b45309', mt: 1.5 }}>
                                No active chatbot is selected. Please pick a chatbot first.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button
                            onClick={goBack}
                            sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                        >
                            Maybe Later
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<GroupAddIcon />}
                            onClick={goToClients}
                            disabled={!activeChatbot}
                            sx={{
                                fontFamily, textTransform: 'none',
                                borderRadius: '10px', bgcolor: '#1e40af',
                                '&:hover': { bgcolor: '#1e3a8a' },
                            }}
                        >
                            Go to CRM Clients
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    return <Outlet />;
}
